import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../../lib/api';
import { formatEuro } from '../../lib/format';
import { ChartEmpty } from './ChartEmpty';

interface Props {
  year: number;
  userId: number | undefined;
}

const MONTHS_LABEL = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
];

export function ChartCAMonthly({ year, userId }: Props): JSX.Element {
  const query = useQuery({
    queryKey: ['dashboard', 'ca-monthly', year, userId],
    queryFn: () => api.dashboard.caMonthly({ year, user_id: userId }),
    staleTime: 60_000,
  });

  if (query.isLoading) {
    return (
      <ChartShell title="CA cumulé mensuel">
        <div className="h-[260px] bg-slate-800/40 rounded animate-pulse" />
      </ChartShell>
    );
  }
  if (query.isError || query.data === undefined) {
    return (
      <ChartShell title="CA cumulé mensuel">
        <ChartEmpty message="Erreur de chargement" />
      </ChartShell>
    );
  }
  const data = query.data.points;
  const totalCumule = data.length > 0 ? (data[data.length - 1]?.ca_cumule_ht_centimes ?? 0) : 0;
  if (data.length === 0 || totalCumule === 0) {
    return (
      <ChartShell title="CA cumulé mensuel">
        <ChartEmpty />
      </ChartShell>
    );
  }

  const chartData = data.map((p) => ({
    mois: MONTHS_LABEL[p.mois - 1] ?? String(p.mois),
    ca_cumule: p.ca_cumule_ht_centimes / 100, // en € pour Recharts
    objectif: p.objectif_cumule_ht_centimes / 100,
  }));

  return (
    <ChartShell title={`CA cumulé mensuel · ${String(year)}`}>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis dataKey="mois" tick={{ fill: '#94a3b8', fontSize: 12 }} stroke="#334155" />
          <YAxis
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            stroke="#334155"
            tickFormatter={(v: number): string =>
              v >= 1000 ? `${(v / 1000).toFixed(0)} k€` : `${String(v)} €`
            }
          />
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}
            labelStyle={{ color: '#e2e8f0' }}
            formatter={(value: number): [string, string] => [formatEuro(value * 100), '']}
          />
          <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 12 }} />
          <Bar dataKey="ca_cumule" name="CA cumulé réel" fill="#1f7a3d" radius={[4, 4, 0, 0]} />
          <Line
            type="monotone"
            dataKey="objectif"
            name="Objectif cumulé"
            stroke="#f59e0b"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartShell>
  );
}

function ChartShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}
